import React, { useState, useCallback, useMemo } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import SendIcon from "@mui/icons-material/Send";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { useRefresh, useUnselectAll, useListContext } from "react-admin";
import { getCookie } from "../../helpers/Cookies";
import { fetchHandleError } from "../../helpers/fetchHandleError";
import { fetch_retry } from "../../helpers/methods";
import Loader from "../Loader";
import { withProfile } from "../DataHooks/useProfile";
import {
  useTakeThreadControl,
  useReleaseThreadControl,
} from "../DataHooks/threadControl";
import { useCustomAllRecords } from "../Common/useCustomAllRecords";
import { MTY_FRAME } from "../Constant";
import { useSendFbMessage } from "../DataHooks/fbMessage";
import { useThrottleCall, COMPLETED } from "../DataHooks/useThrottleCall";
import { Checkbox, FormControlLabel, FormHelperText } from "@mui/material";

const TEXT_TEMPLATE = 0;
const GENERIC_TEMPLATE = 1;
const MEDIA_TEMPLATE = 2;
const GENERIC_MAX_LENGTH = 65;
const GENERIC_STRICT_MAX_LENGTH = 65;

const getMessagePayload = (
  template,
  data,
  url,
  title,
  subtitle,
  img,
  btnText
) => {
  switch (template) {
    case MEDIA_TEMPLATE:
      return {
        message: {
          attachment: {
            type: "template",
            payload: {
              template_type: "media",
              elements: [
                {
                  media_type: url.includes("videos") ? "video" : "image",
                  url,
                  buttons: [{ type: "web_url", title: btnText, url }],
                },
              ],
              sharable: true,
            },
          },
        },
      };
    case GENERIC_TEMPLATE:
      return {
        message: {
          attachment: {
            type: "template",
            payload: {
              template_type: "generic",
              elements: [
                {
                  title,
                  subtitle,
                  image_url: img,
                  default_action: { type: "web_url", url },
                  buttons: [{ type: "web_url", title: btnText, url }],
                },
              ],
            },
          },
        },
      };
    default:
      return { message: { text: data } };
  }
};

const BulkSendWaitList = (props) => {
  const { profile } = props;
  const { resource, selectedIds } = useListContext();
  const allRecords = useCustomAllRecords();
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = React.useState(0);
  const [startSend, setStartSend] = useState(false);
  const [errors, setErrors] = useState([]);
  const [text, setText] = useState(
    "Hi {{name}}, the {{description}} you were waiting for is now back in stock!"
  );
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [useTemplate, setUseTemplate] = useState(TEXT_TEMPLATE);
  const [useMessageTag, setUseMessageTag] = useState(false);
  const [imageUrl, setImageUrl] = useState(MTY_FRAME);
  const [buttonText, setButtonText] = useState("Go to Post");
  const [buttonUrl, setButtonUrl] = useState("");
  const refresh = useRefresh();
  const { trigger: take } = useTakeThreadControl(undefined, {
    onError: (e) => {
      console.log("Take thread control failed:", e.error.message);
    },
  });
  const { trigger: release } = useReleaseThreadControl(undefined, {
    onError: (e) => {
      console.log("Release thread control failed:", e.error.message);
    },
  });
  const { setProcess, status } = useThrottleCall();

  const marketingMessageUrl = `${process.env.REACT_APP_GRAPH_API_URL}/me/messages`;

  const { trigger: baseSendMessage } = useSendFbMessage(marketingMessageUrl);

  const unselectAll = useUnselectAll(resource);
  const loading = pending > 0;

  const removeWaitlists = useCallback(
    (id) => {
      const token = getCookie("token");
      return fetch_retry(
        `${process.env.REACT_APP_CMS_HOST}/waitlists/${id}`,
        {
          method: "DELETE",
          retries: 3,
          retryDelay: 2000,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-type": "application/json; charset=UTF-8",
          },
        },
        3
      )
        .then(fetchHandleError)
        .catch((e) => {
          setErrors((prev) => [
            ...prev,
            { message: "Unable to remove waitlist !" },
          ]);
          alert("Delete WL error");
        })
        .finally(() => {
          setPending((p) => p - 1);
        });
    },
    [setPending]
  );

  const transformText = useCallback(
    ({ text, description = "", key = "", quantity = 1, name = "" }) => {
      return text
        .replace(/{{name}}/g, name)
        .replace(/{{description}}/g, description)
        .replace(/{{key}}/g, key)
        .replace(/{{quantity}}/g, quantity);
    },
    []
  );

  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleDialogClose = useCallback(() => {
    unselectAll();
    setErrors([]);
    setStartSend(false);
    setIsOpen(false);
    setPending(0);
    refresh();
  }, [unselectAll, refresh]);

  React.useEffect(() => {
    if (status === COMPLETED) {
      refresh();
    }
  }, [status, refresh]);

  const handleConfirm = useCallback(() => {
    const rows = allRecords.filter((record) =>
      selectedIds.includes(record._id)
    );

    const calls = rows.map((d) => {
      const msg = transformText({
        text,
        name: d.contact?.name ?? "",
        description: d.description,
        key: d.key,
        quantity: d.quantity,
      });

      const messagePayload = getMessagePayload(
        useTemplate,
        msg,
        buttonUrl,
        transformText({ text: title, ...d }),
        transformText({ text: subtitle, ...d }),
        imageUrl,
        buttonText
      );

      return async () => {
        try {
          try {
            await take({ recipient: { id: d.contact.psid } });
          } catch (e) {
            console.log("Thread control (take) failed:", e);
          }

          await baseSendMessage(
            {
              recipient: {
                id: d.contact.psid,
              },
              messaging_type: useMessageTag ? "MESSAGE_TAG" : "UPDATE",
              ...(useMessageTag ? { tag: "POST_PURCHASE_UPDATE" } : {}),
              ...messagePayload,
            },
            {
              onSuccess: () => {
                removeWaitlists(d._id);
              },
              onError: (err) => {
                setErrors((prevErrors) => [
                  ...prevErrors,
                  {
                    message: `${d.contact?.name ?? "Unknown"}: ${
                      err?.error?.message || "Failed to send message"
                    }`,
                  },
                ]);
                setPending((p) => p - 1);
              },
            }
          );

          try {
            await release({ recipient: { id: d.contact.psid } });
          } catch (e) {
            console.log("Thread control (release) failed:", e);
          }
        } catch (e) {
          // Unexpected failure; silent catch to avoid crashing queue
        }
      };
    });
    if (calls.length === 0) return;

    setPending(rows.length);
    setStartSend(true);
    setProcess(calls);
  }, [
    allRecords,
    setProcess,
    selectedIds,
    transformText,
    text,
    useTemplate,
    buttonUrl,
    title,
    subtitle,
    imageUrl,
    buttonText,
    baseSendMessage,
    useMessageTag,
    take,
    removeWaitlists,
    release,
  ]);

  const previewData = useMemo(() => {
    if (selectedIds.length === 0) return null;
    return allRecords.find((d) => d._id === selectedIds[0]) ?? null;
  }, [selectedIds, allRecords]);

  const generatePreview = useCallback(
    (tpl) =>
      previewData
        ? transformText({
            text: tpl,
            name: previewData.contact?.name ?? "",
            description: previewData.description,
            key: previewData.key,
            quantity: previewData.quantity,
          })
        : "",
    [previewData, transformText]
  );

  const previewText = useMemo(
    () => generatePreview(text),
    [text, generatePreview]
  );
  const previewTitle = useMemo(
    () => generatePreview(title),
    [title, generatePreview]
  );
  const previewSubtitle = useMemo(
    () => generatePreview(subtitle),
    [subtitle, generatePreview]
  );

  const handleTemplateChange = React.useCallback((e) => {
    setUseTemplate(parseInt(e.target.value));
  }, []);

  const isPreviewTitleTooLong = previewTitle.length > GENERIC_STRICT_MAX_LENGTH;
  const isPreviewSubtitleTooLong =
    previewSubtitle.length > GENERIC_STRICT_MAX_LENGTH;

  const refreshPreview = () => {
    const splitted = buttonUrl.split("?")[0].split("/");
    const postId = splitted.pop();

    fetch(
      `${process.env.REACT_APP_GRAPH_API_URL}/${profile.page.psid}_${postId}/attachments?access_token=${profile.page.token}`
    )
      .then(fetchHandleError)
      .then((data) => {
        const url = data?.data?.[0]?.media?.image?.src;
        if (url) {
          setImageUrl(url);
        }
      })
      .catch((err) => {
        console.error("Failed to refresh preview image:", err);
      });
  };

  return (
    <>
      <Button onClick={handleClick}>
        <SendIcon />
        FB
      </Button>
      <Dialog
        // fullScreen={false}
        open={isOpen}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>{`Send FB message`}</DialogTitle>
        <DialogContent>
          {useMessageTag && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                background: "#fff8e1",
                border: "1px solid #ffe082",
                borderRadius: "6px",
              }}
            >
              <strong>⚠️ Waitlist Message Policy:</strong>
              <br />
              Facebook has <strong>removed One-Time Notification (OTN)</strong>.
              We now use regular Facebook messages to notify users.
              <br />
              <br />
              By default, messages can only be sent{" "}
              <strong>within 24 hours</strong> of the user’s last interaction.
              <br />
              To message users <strong>after 24 hours</strong>, we apply a{" "}
              <strong>Message Tag (POST_PURCHASE_UPDATE)</strong> — this is only
              allowed for certain cases.
              <br />
              <br />
              <strong>✅ Allowed (safe examples):</strong>
              <ul style={{ marginTop: 4, marginBottom: 4 }}>
                <li>“The item you waited for is back in stock.”</li>
                <li>“You can now purchase the product you asked about.”</li>
              </ul>
              <strong>❌ Not allowed (may get you blocked):</strong>
              <ul style={{ marginTop: 4, marginBottom: 4 }}>
                <li>“Back in stock — 10% off today only!”</li>
                <li>“Here are other deals you might like.”</li>
              </ul>
              <strong style={{ color: "#c62828" }}>Warning:</strong> Misuse of
              this feature can result in{" "}
              <strong>Facebook blocking your Page</strong> or restricting your
              messaging access.
            </div>
          )}
          <Grid component="label" container alignItems="center" spacing={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="waitlist-panel-native-simple">
                    Message type
                  </InputLabel>
                  <Select
                    native
                    value={useTemplate}
                    onChange={handleTemplateChange}
                    inputProps={{
                      name: "panel",
                      id: "waitlist-panel-native-simple",
                    }}
                  >
                    <option value={TEXT_TEMPLATE}>Text only</option>
                    <option value={GENERIC_TEMPLATE}>
                      Image & text with Post
                    </option>
                    <option value={MEDIA_TEMPLATE}>
                      Image or video with Post
                    </option>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={8}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useMessageTag}
                        disabled={
                          useTemplate !== TEXT_TEMPLATE ||
                          startSend ||
                          selectedIds.length === 0 ||
                          loading
                        }
                        onChange={(e) => setUseMessageTag(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Use Message Tag (POST_PURCHASE_UPDATE)"
                  />
                  <FormHelperText>
                    {useTemplate !== TEXT_TEMPLATE ? (
                      <span style={{ color: "red" }}>
                        Cannot use Message Tag with Generic or Media templates.
                      </span>
                    ) : (
                      <>
                        For messaging <strong>outside</strong> the 24-hour
                        window. Use with care — Facebook may block your page for
                        abuse.
                      </>
                    )}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <h5>Shortcode</h5>
              <ul>
                <li>{`Name: {{name}}`}</li>
                <li>{`Item description: {{description}}`}</li>
                <li>{`Keyword: {{key}}`}</li>
                <li>{`Quantity: {{quantity}}`}</li>
              </ul>
            </Grid>
            {useTemplate === TEXT_TEMPLATE && (
              <>
                <Grid xs={12} md={6}>
                  <h5>Preview</h5>
                  <p>{previewText}</p>
                </Grid>
                <Grid xs={12} md={6}>
                  <TextField
                    label="Message editor"
                    multiline
                    rows={8}
                    value={text}
                    onChange={(e) => {
                      setText(e.target.value);
                    }}
                  />
                </Grid>
              </>
            )}
            {useTemplate === GENERIC_TEMPLATE && (
              <>
                <Grid xs={12} md={6}>
                  <h5>Preview</h5>
                  <Button onClick={refreshPreview}>
                    <RefreshIcon />
                    Refresh image preview
                  </Button>
                  <Card>
                    <CardActionArea>
                      <CardMedia image={imageUrl} component="img" />
                      <CardContent>
                        <Typography gutterBottom variant="h5" component="h2">
                          {previewTitle}
                        </Typography>
                        <p>{previewSubtitle}</p>
                      </CardContent>
                    </CardActionArea>
                    <CardActions style={{ justifyContent: "center" }}>
                      <a
                        href={buttonUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {buttonText}
                      </a>
                    </CardActions>
                  </Card>
                </Grid>
                {isPreviewTitleTooLong && (
                  <p style={{ color: "red" }}>
                    The title is too long, some message could fail !
                  </p>
                )}
                {isPreviewSubtitleTooLong && (
                  <p style={{ color: "red" }}>
                    The subtitle is too long, some message could fail !
                  </p>
                )}
                <Grid sx={{ gridColumn: "span 12" }}>
                  <TextField
                    fullWidth
                    label="FACEBOOK Post URL"
                    rows={1}
                    value={buttonUrl}
                    onChange={(e) => {
                      setButtonUrl(e.target.value);
                    }}
                  />
                </Grid>
                <Grid xs={12} md={12}>
                  <TextField
                    label={`Title(max ${GENERIC_MAX_LENGTH} chars)`}
                    fullWidth
                    helperText={` ${
                      title.length > GENERIC_MAX_LENGTH
                        ? `Too long - ${title.length} `
                        : ""
                    } `}
                    inputProps={{ maxLength: GENERIC_MAX_LENGTH }}
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                    }}
                  />
                </Grid>
                <Grid xs={12} md={12}>
                  <TextField
                    label={`Subtitle(max ${GENERIC_MAX_LENGTH} chars)`}
                    helperText={` ${
                      subtitle.length > GENERIC_MAX_LENGTH
                        ? `Too long - ${subtitle.length} `
                        : ""
                    } `}
                    inputProps={{ maxLength: GENERIC_MAX_LENGTH }}
                    fullWidth
                    value={subtitle}
                    onChange={(e) => {
                      setSubtitle(e.target.value);
                    }}
                  />
                </Grid>
                <Grid xs={12} md={12}>
                  <TextField
                    label="Button label"
                    fullWidth
                    rows={1}
                    value={buttonText}
                    onChange={(e) => {
                      setButtonText(e.target.value);
                    }}
                  />
                </Grid>
              </>
            )}

            {useTemplate === MEDIA_TEMPLATE && (
              <>
                <Grid xs={12} md={6}>
                  <h5>Preview</h5>
                  <Button onClick={refreshPreview}>
                    <RefreshIcon />
                    Refresh image preview
                  </Button>
                  <Card>
                    <CardActionArea>
                      <CardMedia image={imageUrl} component="img" />
                    </CardActionArea>
                    <CardActions style={{ justifyContent: "center" }}>
                      <a
                        href={buttonUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {buttonText}
                      </a>
                    </CardActions>
                  </Card>
                </Grid>
                <Grid sx={{ gridColumn: "span 12" }}>
                  <TextField
                    fullWidth
                    label="FACEBOOK Post URL"
                    rows={1}
                    value={buttonUrl}
                    onChange={(e) => {
                      setButtonUrl(e.target.value);
                    }}
                  />
                </Grid>
                <Grid xs={12} md={12}>
                  <TextField
                    label="Button Editor"
                    fullWidth
                    rows={1}
                    value={buttonText}
                    onChange={(e) => {
                      setButtonText(e.target.value);
                    }}
                  />
                </Grid>
              </>
            )}
            {startSend && pending === 0 && errors.length === 0 && (
              <Grid item xs={12} md={12}>
                <div>Operation completed successfully.</div>
              </Grid>
            )}

            {pending > 0 && errors.length === 0 && (
              <Grid item xs={12} md={12}>
                <div>{`Processing and remaining ${pending}...`}</div>
              </Grid>
            )}
            {errors.length > 0 && (
              <Grid xs={12} md={12}>
                <h5>Errors</h5>
                <div>
                  Something went wrong. Check the errors and retry later.
                </div>
                {errors.map((e, idx) => {
                  return <div key={idx}>{e.message}</div>;
                })}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleConfirm}
            color="primary"
            autoFocus
            disabled={startSend || selectedIds.length === 0 || loading}
          >
            Send
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogClose}
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Loader open={loading}></Loader>
    </>
  );
};

export default withProfile(BulkSendWaitList);
